import { useEffect, useState } from "react";

export type ColorScheme = "light" | "dark";

/**
 * The HTML attribute set by Warp to indicate the active color scheme.
 * This is the source of truth for dark mode, decoupled from `prefers-color-scheme`.
 * @see https://nmp-jira.atlassian.net/browse/DMW-65
 */
const COLOR_SCHEME_ATTRIBUTE = "data-w-theme";

function getColorScheme(): ColorScheme {
  if (typeof document === "undefined") return "light";
  const value = document.documentElement.getAttribute(COLOR_SCHEME_ATTRIBUTE);
  return value === "dark" ? "dark" : "light";
}

export function useColorScheme(): ColorScheme {
  const [colorScheme, setColorScheme] = useState<ColorScheme>(getColorScheme);

  useEffect(() => {
    // Read the initial value on mount (may differ from SSR default)
    setColorScheme(getColorScheme());

    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === "attributes" && mutation.attributeName === COLOR_SCHEME_ATTRIBUTE) {
          setColorScheme(getColorScheme());
          break;
        }
      }
    });

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: [COLOR_SCHEME_ATTRIBUTE],
    });

    return () => observer.disconnect();
  }, []);

  return colorScheme;
}
